import {
    IndexSymbol,
    OptionType,
    AnalysisMode,
    TimeGap,
    UserRole,
    UserStatus,
    SUPPORTED_SYMBOLS,
    SYMBOL_DISPLAY_NAMES,
    SYMBOL_METADATA,
    SYMBOL_VARIANTS,
    UPSTOX_API_CONFIG as SHARED_UPSTOX_API_CONFIG,
    UPSTOX_INSTRUMENT_KEYS,
    STRIKE_STEP,
    API_CONFIG as SHARED_API_CONFIG,
    HF_CONFIG as SHARED_HF_CONFIG,
    COLORS,
    MARKET_HOURS as SHARED_MARKET_HOURS,
    MARKET_HOURS_CONFIG as SHARED_MARKET_HOURS_CONFIG,
    isMarketDay as sharedIsMarketDay,
    isMarketHours as sharedIsMarketHours,
    isAtMarketClose as sharedIsAtMarketClose,
    isFullDayRange as sharedIsFullDayRange,
    type MarketHourItem,
    type MarketMilestone,
    REFRESH_INTERVALS as SHARED_REFRESH_INTERVALS,
    STORAGE_KEYS as SHARED_STORAGE_KEYS,
    getCanonicalSymbol as sharedGetCanonicalSymbol,
    getStrikeStepForSymbol as sharedGetStrikeStepForSymbol,
    normalizeSymbolLookupKey as sharedNormalizeSymbolLookupKey,
    getSymbolLookupCandidates as sharedGetSymbolLookupCandidates,
    isSymbolLookupMatch as sharedIsSymbolLookupMatch,
    INDEX_CONSTITUENT_SYMBOLS,
    INDEX_CONSTITUENT_WEIGHTS,
    INDEX_EXCHANGE_MAP,
    INDEX_SPOT_TICKERS,
    getIndexExchange,
    formatTvTicker,
    getFormattedTickers,
    getAllIndexFormattedTickers,
    TRADINGVIEW_SCANNER_URL,
    DIRECT_TRADINGVIEW_SCANNER_URL,
    MONEYCONTROL_INDEX_IDS,
    MONEYCONTROL_MARKETMAP_URL,
    DIRECT_MONEYCONTROL_MARKETMAP_URL,
    normalizeMoneycontrolSymbol,
    type StockQuote,
    type IndexConstituentsSummary,
    TRADING_PSYCHOLOGY_CONFIG,
    type PsychologyAvoidItem,
    PLAYBACK_SPEEDS,
    PLAYBACK_SPEED_LABELS,
    PLAYBACK_SPEED_CONFIG,
    DEFAULT_PLAYBACK_SPEED,
    type PlaybackSpeed,
    FUTURES_FEED_CONFIG,
    type FutureQuoteData,
} from '@options-oracle/types'


export {
    IndexSymbol,
    OptionType,
    AnalysisMode,
    TimeGap,
    UserRole,
    UserStatus,
    SUPPORTED_SYMBOLS,
    SYMBOL_DISPLAY_NAMES,
    SYMBOL_METADATA,
    SYMBOL_VARIANTS,
    UPSTOX_INSTRUMENT_KEYS,
    STRIKE_STEP,
    COLORS,
    INDEX_CONSTITUENT_SYMBOLS,
    INDEX_CONSTITUENT_WEIGHTS,
    INDEX_EXCHANGE_MAP,
    INDEX_SPOT_TICKERS,
    getIndexExchange,
    formatTvTicker,
    getFormattedTickers,
    getAllIndexFormattedTickers,
    TRADINGVIEW_SCANNER_URL,
    DIRECT_TRADINGVIEW_SCANNER_URL,
    MONEYCONTROL_INDEX_IDS,
    MONEYCONTROL_MARKETMAP_URL,
    DIRECT_MONEYCONTROL_MARKETMAP_URL,
    normalizeMoneycontrolSymbol,
    type StockQuote,
    type IndexConstituentsSummary,
    TRADING_PSYCHOLOGY_CONFIG,
    type PsychologyAvoidItem,
    type MarketHourItem,
    type MarketMilestone,
    PLAYBACK_SPEEDS,
    PLAYBACK_SPEED_LABELS,
    PLAYBACK_SPEED_CONFIG,
    DEFAULT_PLAYBACK_SPEED,
    type PlaybackSpeed,
    FUTURES_FEED_CONFIG,
    type FutureQuoteData,
}








// ============================================================================
// 2. UPSTOX JSON API CONFIGURATION (Web Specific Proxy)
// ============================================================================
export const UPSTOX_API_CONFIG = {
    ...SHARED_UPSTOX_API_CONFIG,
    PROXY_URL: '/api/upstox',
    getApiUrl: (symbol: IndexSymbol, expiry: string): string => {
        const config = SHARED_UPSTOX_API_CONFIG.symbols[symbol]
        if (!config) throw new Error(`Unsupported symbol: ${symbol}`)

        const isServer = typeof window === 'undefined'
        const baseUrl = isServer ? SHARED_UPSTOX_API_CONFIG.REAL_URL : UPSTOX_API_CONFIG.PROXY_URL

        return `${baseUrl}?assetKey=${config.assetKeyEncoded}&strategyChainType=PC_CHAIN&expiry=${expiry}`
    },
}

// ============================================================================
// 2.2 UPSTOX v3 HISTORICAL CANDLE API CONFIGURATION
// ============================================================================
export const UPSTOX_CANDLE_API = {
    BASE_URL_V3: 'https://api.upstox.com/v3/historical-candle',
    BASE_URL_V2: 'https://api.upstox.com/v2/historical-candle',
    // v3 interval format (for NSE_INDEX keys)
    INTERVAL_MAP_V3: {
        '1minute': 'minutes/1',
        '3minute': 'minutes/3',
        '5minute': 'minutes/5',
        '15minute': 'minutes/15',
        '30minute': 'minutes/30',
        '60minute': 'hours/1',
        day: 'days/1',
        week: 'weeks/1',
        month: 'months/1',
    } as Record<string, string>,
    // v2 interval map kept as-is (v2 accepts 1minute, day etc. directly)
    INTERVAL_MAP: {
        '1minute': 'minutes/1',
        '3minute': 'minutes/3',
        '5minute': 'minutes/5',
        '15minute': 'minutes/15',
        '30minute': 'minutes/30',
        '60minute': 'hours/1',
        day: 'days/1',
        week: 'weeks/1',
        month: 'months/1',
    } as Record<string, string>,
    /**
     * NSE_FO|<number> (token IDs from PC_CHAIN analytics) only work with v2 API.
     * NSE_INDEX|... keys work with v3 API.
     */
    _isTokenKey: (instrumentKey: string): boolean => {
        return instrumentKey.startsWith('NSE_FO|') || instrumentKey.startsWith('BSE_FO|')
    },
    getHistoricalUrl: (instrumentKey: string, interval: string, toDate: string, fromDate: string): string => {
        const encoded = encodeURIComponent(instrumentKey)
        if (UPSTOX_CANDLE_API._isTokenKey(instrumentKey)) {
            // v2: /v2/historical-candle/{key}/{interval}/{to}/{from}
            // Upstox v2 only supports 1minute, 30minute, day, week, month for options tokens
            const v2Interval = (interval === '5minute' || interval === '3minute' || interval === '15minute') ? '1minute' : interval
            return `${UPSTOX_CANDLE_API.BASE_URL_V2}/${encoded}/${v2Interval}/${toDate}/${fromDate}`
        }
        const v3Interval = UPSTOX_CANDLE_API.INTERVAL_MAP_V3[interval] || 'minutes/1'
        return `${UPSTOX_CANDLE_API.BASE_URL_V3}/${encoded}/${v3Interval}/${toDate}/${fromDate}`
    },
    getIntradayUrl: (instrumentKey: string, interval: string = '1minute'): string => {
        const encoded = encodeURIComponent(instrumentKey)
        if (UPSTOX_CANDLE_API._isTokenKey(instrumentKey)) {
            // v2: /v2/historical-candle/intraday/{key}/{interval}
            const v2Interval = (interval === '5minute' || interval === '3minute' || interval === '15minute') ? '1minute' : interval
            return `${UPSTOX_CANDLE_API.BASE_URL_V2}/intraday/${encoded}/${v2Interval}`
        }
        const v3Interval = UPSTOX_CANDLE_API.INTERVAL_MAP_V3[interval] || 'minutes/1'
        return `${UPSTOX_CANDLE_API.BASE_URL_V3}/intraday/${encoded}/${v3Interval}`
    },
}

export const REFRESH_INTERVALS = SHARED_REFRESH_INTERVALS
export const STORAGE_KEYS = SHARED_STORAGE_KEYS
export const MARKET_HOURS = SHARED_MARKET_HOURS
export const MARKET_HOURS_CONFIG = SHARED_MARKET_HOURS_CONFIG
export const API_CONFIG = SHARED_API_CONFIG
export const HF_CONFIG = SHARED_HF_CONFIG

export function isMarketDay(date?: Date): boolean {
    return sharedIsMarketDay(date)
}

export function isMarketHours(): boolean {
    return sharedIsMarketHours()
}

export const isAtMarketClose = sharedIsAtMarketClose
export const isFullDayRange = sharedIsFullDayRange

export const getCanonicalSymbol = sharedGetCanonicalSymbol
export const getStrikeStepForSymbol = sharedGetStrikeStepForSymbol
export const normalizeSymbolLookupKey = sharedNormalizeSymbolLookupKey
export const getSymbolLookupCandidates = sharedGetSymbolLookupCandidates
export const isSymbolLookupMatch = sharedIsSymbolLookupMatch

// Legacy CONFIG bundle (Pruned)
export const CONFIG = {
    SYMBOLS: SUPPORTED_SYMBOLS,
    SYMBOL_NAMES: SYMBOL_DISPLAY_NAMES,
    SYMBOL_METADATA: SYMBOL_METADATA,
    SYMBOL_VARIANTS: SYMBOL_VARIANTS,
    UPSTOX_API: UPSTOX_API_CONFIG,
    STRIKE_STEP: STRIKE_STEP,
    API: API_CONFIG,
    HF: SHARED_HF_CONFIG,
    COLORS,
    MARKET: MARKET_HOURS,
    REFRESH: REFRESH_INTERVALS,
    UI: {
        ROWS_PER_PAGE_OPTIONS: [10, 20, 50, 100],
        DEFAULT_ROWS_PER_PAGE: 10,
        TOAST_DURATION: 3000,
        STORAGE_KEYS: STORAGE_KEYS,
    },
}

// ============================================================================
// 3. LANDING PAGE CONFIGURATION & ARCHITECTURE (Centralized)
// ============================================================================
export interface LandingStrikeData {
    strike: number
    ceOi: string
    ceChg: string
    peOi: string
    peChg: string
    isSupport?: boolean
    isResistance?: boolean
    isAtm?: boolean
}

export interface LandingSymbolSimulation {
    symbol: string
    spotPrice: string
    spotChange: string
    spotChangePct: string
    isPositive: boolean
    atmStrike: number
    strikeStep: number
    resistanceStrike: number
    supportStrike: number
    shift: number
    pcr: string
    sentiment: string
    strikes: LandingStrikeData[]
}

export const LANDING_PAGE_CONFIG = {
    NAV_ITEMS: [
        { label: 'Capabilities', href: '#features' },
        { label: 'Live Cockpit', href: '#cockpit' },
        { label: 'Architecture', href: '#architecture' },
        { label: 'Performance', href: '#performance' },
    ],
    STATS: [
        { value: '< 2.0s', label: 'Feed Latency', desc: 'Direct sub-second real-time streaming' },
        { value: '100%', label: 'Dynamic ATM Alignment', desc: 'Automated strike gap calibration' },
        { value: '1-Min', label: 'Intraday Cloud Sync', desc: 'Continuous historical snapshot archive' },
        { value: '99.9%', label: 'Terminal Availability', desc: 'High-availability Cloudflare deployment' },
    ],
    PILLARS: [
        {
            number: '01',
            tag: 'LIVE FEED',
            title: 'Real-Time Strategy Chain Streaming',
            desc: 'Sub-2-second streaming updates directly from live derivative market feeds. Dynamic ATM calculations center strike ranges instantaneously to give you clear institutional visibility.',
        },
        {
            number: '02',
            tag: 'ALGO MATRIX',
            title: 'Algorithmic Support, Resistance & Shift',
            desc: 'Clustering top-3 Call and Put Open Interest and volume metrics. Highlights critical red Resistance and green Support zones with real-time dynamic Shift midpoint calculations.',
        },
        {
            number: '03',
            tag: 'TIME ENGINE',
            title: 'Continuous 1-Minute Intraday Archiving',
            desc: 'Every minute snapshot is archived directly to high-speed cloud infrastructure. Replay market sessions, test crossover momentum triggers, and track OI percentage variance.',
        },
    ],
    SIMULATION: {
        NIFTY: {
            symbol: 'NIFTY',
            spotPrice: '24,850.45',
            spotChange: '+142.30',
            spotChangePct: '+0.58%',
            isPositive: true,
            atmStrike: 24850,
            strikeStep: 50,
            resistanceStrike: 25000,
            supportStrike: 24700,
            shift: 24850,
            pcr: '0.94',
            sentiment: 'Bullish Momentum',
            strikes: [
                { strike: 24700, ceOi: '8.4L', ceChg: '-14.2%', peOi: '24.8L', peChg: '+38.5%', isSupport: true, isAtm: false, isResistance: false },
                { strike: 24750, ceOi: '11.2L', ceChg: '-8.5%', peOi: '18.1L', peChg: '+22.4%', isSupport: false, isAtm: false, isResistance: false },
                { strike: 24800, ceOi: '19.5L', ceChg: '+4.1%', peOi: '22.3L', peChg: '+18.9%', isSupport: false, isAtm: false, isResistance: false },
                { strike: 24850, ceOi: '28.4L', ceChg: '+32.6%', peOi: '26.9L', peChg: '+29.4%', isSupport: false, isAtm: true, isResistance: false },
                { strike: 24900, ceOi: '25.1L', ceChg: '+24.8%', peOi: '14.2L', peChg: '-5.2%', isSupport: false, isAtm: false, isResistance: false },
                { strike: 24950, ceOi: '19.8L', ceChg: '+18.2%', peOi: '9.5L', peChg: '-12.0%', isSupport: false, isAtm: false, isResistance: false },
                { strike: 25000, ceOi: '34.2L', ceChg: '+42.5%', peOi: '7.8L', peChg: '-18.6%', isSupport: false, isAtm: false, isResistance: true },
            ],
        },
        BANKNIFTY: {
            symbol: 'BANKNIFTY',
            spotPrice: '52,420.80',
            spotChange: '+285.40',
            spotChangePct: '+0.55%',
            isPositive: true,
            atmStrike: 52400,
            strikeStep: 100,
            resistanceStrike: 52800,
            supportStrike: 52000,
            shift: 52400,
            pcr: '1.02',
            sentiment: 'Strong Bias',
            strikes: [
                { strike: 52000, ceOi: '9.2L', ceChg: '-18.0%', peOi: '28.4L', peChg: '+44.1%', isSupport: true, isAtm: false, isResistance: false },
                { strike: 52200, ceOi: '14.5L', ceChg: '-6.2%', peOi: '20.6L', peChg: '+25.0%', isSupport: false, isAtm: false, isResistance: false },
                { strike: 52400, ceOi: '26.8L', ceChg: '+31.4%', peOi: '25.2L', peChg: '+28.3%', isSupport: false, isAtm: true, isResistance: false },
                { strike: 52600, ceOi: '22.1L', ceChg: '+19.5%', peOi: '12.4L', peChg: '-8.5%', isSupport: false, isAtm: false, isResistance: false },
                { strike: 52800, ceOi: '31.5L', ceChg: '+39.2%', peOi: '6.9L', peChg: '-22.1%', isSupport: false, isAtm: false, isResistance: true },
            ],
        },
        SENSEX: {
            symbol: 'SENSEX',
            spotPrice: '81,650.25',
            spotChange: '+390.15',
            spotChangePct: '+0.48%',
            isPositive: true,
            atmStrike: 81600,
            strikeStep: 100,
            resistanceStrike: 82000,
            supportStrike: 81200,
            shift: 81600,
            pcr: '0.98',
            sentiment: 'Balanced Flow',
            strikes: [
                { strike: 81200, ceOi: '4.1L', ceChg: '-12.0%', peOi: '12.6L', peChg: '+36.2%', isSupport: true, isAtm: false, isResistance: false },
                { strike: 81400, ceOi: '7.8L', ceChg: '-4.1%', peOi: '10.2L', peChg: '+19.5%', isSupport: false, isAtm: false, isResistance: false },
                { strike: 81600, ceOi: '14.2L', ceChg: '+28.1%', peOi: '13.9L', peChg: '+24.4%', isSupport: false, isAtm: true, isResistance: false },
                { strike: 81800, ceOi: '11.5L', ceChg: '+15.2%', peOi: '6.4L', peChg: '-7.0%', isSupport: false, isAtm: false, isResistance: false },
                { strike: 82000, ceOi: '16.9L', ceChg: '+34.8%', peOi: '3.8L', peChg: '-19.4%', isSupport: false, isAtm: false, isResistance: true },
            ],
        },
    } as Record<string, LandingSymbolSimulation>,
    SHOWCASE_MODULES: [
        {
            id: 'chain',
            title: 'Real-Time Strategy Ladder',
            subtitle: 'Sub-2-second options chain decoding with automatic ATM centering and strike step recognition.',
        },
        {
            id: 'levels',
            title: 'Support, Resistance & Shift Matrix',
            subtitle: 'Algorithmic identification of key institutional liquidity walls and dynamic midpoint shifts.',
        },
        {
            id: 'history',
            title: '1-Minute Intraday Replay',
            subtitle: 'Time-machine precision backtesting with historical snapshot synchronization.',
        },
    ],
    FAQS: [
        {
            question: 'How fast is the real-time options data stream?',
            answer: 'The terminal connects to live derivative strategy chain feeds, refreshing open interest and volume metrics continuously every 2 seconds without requiring manual page reloads.',
        },
        {
            question: 'How are Support & Resistance strikes calculated?',
            answer: 'Support Strike is derived from the top-3 highest Put Open Interest or Volume strike levels (colored Green), while Resistance Strike is derived from top-3 Call Open Interest or Volume levels (colored Red). The Shift is calculated dynamically as (Resistance Strike + Support Strike) / 2.',
        },
        {
            question: 'What symbols and index steps are supported?',
            answer: 'The platform specializes in high-liquidity Indian benchmark indices: NIFTY (50 strike step), BANKNIFTY (100 strike step), and SENSEX (100 strike step) with automated dynamic ATM detection.',
        },
        {
            question: 'How does Intraday History sync work?',
            answer: 'Every minute during market hours, a complete snapshot of all strikes and analytics is archived into cloud storage, enabling seamless intraday historical analysis and variance tracking.',
        },
    ],
}

export const LOGIN_PAGE_CONFIG = {
    GATEWAY_TITLE: 'QUANT DESK GATEWAY',
    GATEWAY_SUBTITLE: '256-BIT SSL ENCRYPTED',
    VERSION_BADGE: 'v2.0 PRO',
    HERO_TITLE: 'Institutional Options',
    HERO_ACCENT: 'Precision Terminal',
    HERO_DESCRIPTION: 'Direct low-latency options chain analytics, automated dynamic ATM alignment, institutional OI clustering, and 1-minute historical intraday playback.',
    AUTH_HEADER: 'GATEWAY // SECURE ACCESS',
    TITLE_SIGN_IN: 'Sign In to Terminal',
    TITLE_SIGN_UP: 'Request Desk Clearance',
    SUBTITLE_SIGN_IN: 'Access real-time quantitative options workspace.',
    SUBTITLE_SIGN_UP: 'New trading accounts require Administrator approval prior to activation.',
    GOOGLE_BUTTON_TEXT: 'Continue with Google',
    POPUP_REDIRECT_TEXT: 'Popup blocked? Direct redirect',
    FOOTER_ASSURANCE: 'Mandatory Administrator Approval • 256-Bit SSL Enforced',
    BOTTOM_RIBBON_LEFT: 'OPTIONS ORACLE TERMINAL // SECURE CLEARANCE SYSTEM',
    BOTTOM_RIBBON_RIGHT: 'ADMIN CLEARANCE ENFORCED',
    FEATURES: [
        {
            title: 'Sub-2s Direct Real-Time Streaming',
            desc: 'Continuous options strategy chain calculations without drift.',
        },
        {
            title: 'Algorithmic Support & Resistance',
            desc: 'Identifies top-3 Call & Put volume and open interest liquidity walls.',
        },
        {
            title: 'Role-Based Desk Clearance Protocol',
            desc: 'Mandatory administrator clearance protects quantitative execution.',
        },
    ],
    INDICES: [
        { symbol: 'NIFTY 50', price: '24,350.25', change: '+0.42%', step: '50' },
        { symbol: 'BANKNIFTY', price: '52,180.10', change: '+0.65%', step: '100' },
        { symbol: 'SENSEX', price: '80,410.80', change: '+0.38%', step: '100' },
    ],
    TELEMETRY: [
        { label: 'DESK LATENCY', value: '< 12ms' },
        { label: 'ENCRYPTION', value: 'TLS 1.3' },
        { label: 'FEED STATUS', value: 'OPERATIONAL' },
    ],
}
